import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  RefreshCw,
  ZapOff,
  Zap,
  Repeat,
  SpellCheck,
} from 'lucide-angular';
import { AssignmentService } from '../../../services/assignment.service';
import { AssignmentApiService } from '../../../services/assignment-api.service';
import {
  AssignmentQuestion,
  MultipleChoiceOption,
} from '../../../services/assignment.models';
import {
  ScoringGuideStandard,
  ScoringItemPreview,
} from '../../../../grading-chat/grading.types';
import { EditQuestionSheetComponent } from './edit-question-sheet';

export interface EditQuestionSheetData {
  question: AssignmentQuestion;
}

/** Maps quick-action labels to the instruction text sent to the AI. */
const QUICK_ACTION_INSTRUCTIONS: Record<string, string> = {
  Regenerate:
    'Generate a completely new question of the same type on the same topic. The question should be different from the original.',
  Simplify:
    'Simplify this question to make it easier for students while keeping the same topic.',
  'Make harder':
    'Make this question more challenging while keeping the same topic.',
  Rephrase:
    'Rephrase this question while keeping the same difficulty and intent.',
  'Fix grammar':
    'Fix any grammar or spelling errors in this question and its options.',
};

@Injectable()
export class QuestionEditService {
  private readonly assignmentService = inject(AssignmentService);
  private readonly apiService = inject(AssignmentApiService);
  private readonly data =
    inject<EditQuestionSheetData>(MAT_BOTTOM_SHEET_DATA);
  private readonly snackBar = inject(MatSnackBar);
  private readonly bottomSheetRef = inject(
    MatBottomSheetRef<EditQuestionSheetComponent>,
  );

  /** Emits whenever async state changes (e.g. AI loading completes). */
  readonly stateChanged$ = new Subject<void>();

  // ============================================
  // STATE
  // ============================================

  /** Deep clone of the question to avoid mutating the source. */
  editingQuestion: AssignmentQuestion = {
    ...this.data.question,
    options: this.data.question.options.map((o) => ({ ...o })),
  };

  readonly questionIndex: number =
    this.assignmentService.questions.findIndex(
      (q) => q.id === this.data.question.id,
    ) + 1;

  isSaving = false;
  aiLoading = false;
  aiInstruction = '';

  // Rubric state
  rubric: ScoringItemPreview | null = null;
  rubricStandards: ScoringGuideStandard[] = [];
  rubricLoading = false;
  rubricLoaded = false;

  readonly quickActions = [
    { label: 'Regenerate', icon: Repeat },
    { label: 'Simplify', icon: ZapOff },
    { label: 'Make harder', icon: Zap },
    { label: 'Rephrase', icon: RefreshCw },
    { label: 'Fix grammar', icon: SpellCheck },
  ];

  // ============================================
  // SAVE & CANCEL
  // ============================================

  /**
   * Save the question to backend and regenerate its scoring guide item.
   * Synchronous — waits for both operations to complete before closing.
   */
  save(): void {
    const assignmentId = this.assignmentService.assignmentId;
    if (!assignmentId || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.stateChanged$.next();

    this.apiService
      .saveQuestion(assignmentId, this.editingQuestion.id, this.editingQuestion)
      .subscribe({
        next: (response) => {
          // Update the question in shared state with the new scoringGuideItemId
          const savedQuestion: AssignmentQuestion = {
            ...this.editingQuestion,
            scoringGuideItemId: response.scoringGuideItemId,
          };
          this.assignmentService.replaceQuestionLocally(savedQuestion);

          this.isSaving = false;
          this.stateChanged$.next();

          this.snackBar.open('Question saved!', 'Dismiss', {
            duration: 3000,
            verticalPosition: 'top',
          });

          this.bottomSheetRef.dismiss();
        },
        error: () => {
          this.isSaving = false;
          this.stateChanged$.next();
          this.snackBar.open(
            'Failed to save question. Please try again.',
            'Dismiss',
            { duration: 4000, verticalPosition: 'top' },
          );
        },
      });
  }

  /**
   * Discard local changes and close the sheet.
   */
  cancel(): void {
    this.bottomSheetRef.dismiss();
  }

  // ============================================
  // OPTION EDITING (local only)
  // ============================================

  isLastCorrectOption(optionIndex: number): boolean {
    const option = this.editingQuestion.options[optionIndex];
    if (!option.isCorrect) {
      return false;
    }
    const correctCount = this.editingQuestion.options.filter(
      (o) => o.isCorrect,
    ).length;
    return correctCount === 1;
  }

  toggleOptionCorrect(optionIndex: number): void {
    if (this.isLastCorrectOption(optionIndex)) {
      return;
    }
    this.editingQuestion.options[optionIndex].isCorrect =
      !this.editingQuestion.options[optionIndex].isCorrect;
  }

  moveOption(index: number, direction: 'up' | 'down'): void {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (
      targetIndex < 0 ||
      targetIndex >= this.editingQuestion.options.length
    ) {
      return;
    }

    const options = [...this.editingQuestion.options];
    [options[index], options[targetIndex]] = [
      options[targetIndex],
      options[index],
    ];
    this.editingQuestion.options = options;
  }

  deleteOption(index: number): void {
    this.editingQuestion.options = this.editingQuestion.options.filter(
      (_, i) => i !== index,
    );
  }

  addOption(): void {
    const newOption: MultipleChoiceOption = {
      id: `opt_${Date.now()}_${Math.random().toString(36).substring(2, 5)}`,
      text: '',
      isCorrect: false,
    };
    this.editingQuestion.options = [
      ...this.editingQuestion.options,
      newOption,
    ];
  }

  // ============================================
  // AI ACTIONS (local only — no backend persist)
  // ============================================

  submitAiInstruction(): void {
    const instruction = this.aiInstruction.trim();
    if (!instruction) {
      return;
    }
    this.executeAiAction(instruction);
    this.aiInstruction = '';
  }

  applyQuickAction(action: string): void {
    const instruction = QUICK_ACTION_INSTRUCTIONS[action];
    if (!instruction) {
      return;
    }
    this.executeAiAction(instruction);
  }

  private executeAiAction(instruction: string): void {
    const assignmentId = this.assignmentService.assignmentId;
    if (!assignmentId) {
      return;
    }

    this.aiLoading = true;
    this.stateChanged$.next();

    this.apiService
      .applyQuestionAiAction(
        assignmentId,
        this.editingQuestion.id,
        instruction,
      )
      .subscribe({
        next: (response) => {
          // Update the local clone only — nothing is persisted until Save
          this.editingQuestion = {
            ...response.question,
            options: response.question.options.map((o) => ({ ...o })),
          };

          this.aiLoading = false;
          this.stateChanged$.next();
        },
        error: () => {
          this.aiLoading = false;
          this.stateChanged$.next();
          this.snackBar.open(
            'AI action failed. Please try again.',
            'Dismiss',
            { duration: 4000, verticalPosition: 'top' },
          );
        },
      });
  }

  // ============================================
  // RUBRIC (read-only view)
  // ============================================

  /**
   * Load the rubric for the current question.
   * If the question has no scoringGuideItemId, shows empty state immediately.
   * Otherwise fetches the scoring guide item from the backend.
   */
  loadRubric(): void {
    if (this.rubricLoaded || this.rubricLoading) {
      return;
    }

    const itemId = this.editingQuestion.scoringGuideItemId;

    // No scoring guide item — show empty state immediately, no API call
    if (!itemId) {
      this.rubric = null;
      this.rubricLoading = false;
      this.rubricLoaded = true;
      this.stateChanged$.next();
      return;
    }

    const assignmentId = this.assignmentService.assignmentId;
    if (!assignmentId) {
      return;
    }

    this.rubricLoading = true;
    this.stateChanged$.next();

    this.apiService
      .getScoringGuideItems(assignmentId, [itemId])
      .subscribe({
        next: (response) => {
          this.rubric = response.items[0] ?? null;
          this.rubricStandards = response.standards ?? [];
          this.rubricLoading = false;
          this.rubricLoaded = true;
          this.stateChanged$.next();
        },
        error: () => {
          this.rubricLoading = false;
          this.rubricLoaded = true;
          this.stateChanged$.next();
        },
      });
  }
}
